import { SkyDialog, SkyMessageBar } from "@skypointcloud/skypoint-ui";
import { ROLE_LABELS } from "@shared/permissions";
import type { AccessRow } from "@/hooks/use-access";
import { usePermissions } from "@/hooks/use-permissions";
import { InlineErrorBar, type InlineFailure } from "@/lib/feedback/InlineErrorBar";

/**
 * Revoking one grant (canvas 07, "Revoke confirm").
 *
 * The title names the person AND the scope, so a screen reader hears what is
 * being revoked rather than "Are you sure?". The body says what actually happens
 * (B-11): this one grant ends, the person's other grants and the account stay, and
 * deactivating the account is what ends all access. #1287: only a person who holds
 * `platform.tenants.manage` has the Platform tab, so only they are sent there. Everyone else reads
 * that a platform admin can deactivate the account, and on your own grant the sentence is left
 * out (you are not ending your own account from this dialog) and the body speaks to you. An open session
 * stops working at the next permission check rather than immediately (FR-11).
 *
 * B-37: on your own row the dialog says so, and the button is relabelled, as
 * `ResetSignInDialog` does; the server's last-admin guard is platform-only by design.
 *
 * Session 10 (ux-s10 §3): the dialog stays open until the revoke lands (the parent
 * clears `row`), so a refusal is said inside it rather than behind the modal.
 */
export function RevokeGrantDialog({
  row,
  scopeName,
  isSubmitting,
  onConfirm,
  onOpenChange,
  error = null,
}: {
  row: AccessRow | null;
  scopeName: string;
  isSubmitting: boolean;
  onConfirm: (row: AccessRow) => void;
  onOpenChange: (open: boolean) => void;
  /** The last refusal, shown inside the dialog. */
  error?: InlineFailure | null;
}) {
  const isSelf = row?.isSelf === true;
  const { can } = usePermissions();
  // A person who is already deactivated cannot sign in, so the sentence would be untrue for them.
  const deactivateSentence =
    row?.userStatus === "disabled"
      ? " The account is already deactivated, so they cannot sign in."
      : can("platform.tenants.manage")
        ? " To end all their access, deactivate the account from the Platform tab."
        : " To end all their access, a platform admin can deactivate the account.";
  return (
    <SkyDialog
      open={row !== null}
      onOpenChange={onOpenChange}
      onBeforeClose={(reason) => reason !== "primaryAction"}
      size="medium"
      testId="revoke-dialog"
      title={
        row
          ? isSelf
            ? `Revoke your own ${ROLE_LABELS[row.roleName]} in ${scopeName}`
            : `Revoke ${ROLE_LABELS[row.roleName]} from ${row.email} in ${scopeName}`
          : "Revoke access"
      }
      description={
        isSelf
          ? "Only this grant is revoked. Any other grants you hold and your account stay, and your history stays in the audit trail."
          : `Only this grant is revoked. Any other grants the person holds and their account stay, and their history stays in the audit trail.${deactivateSentence}`
      }
      primaryAction={{
        label: isSelf ? "Revoke my access" : "Revoke access",
        destructive: true,
        loading: isSubmitting,
        onClick: () => {
          if (row) onConfirm(row);
        },
      }}
      secondaryAction={{ label: "Keep access", onClick: () => onOpenChange(false) }}
    >
      <div className="flex flex-col gap-3">
        {isSelf ? (
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            testId="revoke-self-warning"
            title="This is your own access"
            message="You lose it at your next request, and only an admin above this scope can give it back."
          />
        ) : (
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            title="They lose this access at their next request"
            message="An open session stops working as soon as the server next checks their permissions."
          />
        )}
        <InlineErrorBar
          error={error}
          title={error?.retryable ? undefined : "The access was not revoked"}
          testId="revoke-error"
        />
      </div>
    </SkyDialog>
  );
}
